/**
 * Message composer — card (default) or pill (planning style).
 * Fixed height: input scrolls internally; message pane keeps full area above.
 */

/** Single-line pill input height; grows only via internal textarea scroll. */
const PILL_INPUT_HEIGHT_CLASS = 'h-10 max-h-10 min-h-10';
/** Card variant textarea — fixed block; Shift+Enter adds lines inside the box. */
const CARD_INPUT_HEIGHT_CLASS = 'h-20 max-h-20 min-h-20';

import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { ConversationModelOption } from './ConversationShell';

export type ConversationComposerVariant = 'card' | 'pill';

interface ConversationComposerProps {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  models?: ConversationModelOption[];
  selectedModelId?: string;
  onModelChange?: (modelId: string) => void;
  className?: string;
  variant?: ConversationComposerVariant;
  /** Rotating hint overlay (pill variant, empty input). */
  hintText?: string | null;
  showHintOverlay?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
}

function SendArrowIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

export const ConversationComposer: React.FC<ConversationComposerProps> = ({
  onSend,
  disabled = false,
  placeholder,
  models,
  selectedModelId,
  onModelChange,
  className = '',
  variant = 'card',
  hintText,
  showHintOverlay = false,
  onFocus,
  onBlur,
}) => {
  const { t } = useLanguage();
  const [value, setValue] = useState('');

  const canSend = !disabled && value.trim().length > 0;

  const submit = () => {
    if (!canSend) return;
    onSend(value.trim());
    setValue('');
  };

  if (variant === 'pill') {
    const useHintOverlay = showHintOverlay && Boolean(hintText) && !value.trim();
    return (
      <form
        className={`flex-shrink-0 border-t border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 pb-3 pt-2 ${className}`}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex items-end gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-surface-bg)] py-1.5 pl-1.5 pr-1.5 shadow-lg ring-1 ring-black/5">
          <div className="relative min-w-0 flex-1 self-center">
            {useHintOverlay && (
              <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden px-1 py-2" aria-hidden>
                <p className="animate-planning-hint-enter line-clamp-2 text-left text-base leading-snug text-[var(--color-surface-text-secondary)] opacity-90">
                  {hintText}
                </p>
              </div>
            )}
            <label htmlFor="ai-studio-composer-pill" className="sr-only">
              {t('aiStudio.conversation.composerLabel')}
            </label>
            <textarea
              id="ai-studio-composer-pill"
              value={value}
              rows={1}
              onChange={(e) => setValue(e.target.value)}
              onFocus={onFocus}
              onBlur={onBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder={useHintOverlay ? '' : (placeholder ?? t('aiStudio.conversation.placeholder'))}
              disabled={disabled}
              className={
                useHintOverlay
                  ? `relative z-10 w-full resize-none overflow-y-auto border-0 bg-transparent px-1 py-2 text-base leading-snug text-transparent caret-[var(--color-surface-text)] placeholder:text-transparent focus:outline-none focus:ring-0 disabled:opacity-50 ${PILL_INPUT_HEIGHT_CLASS}`
                  : `relative z-10 w-full resize-none overflow-y-auto border-0 bg-transparent px-1 py-2 text-base leading-snug text-[var(--color-surface-text)] placeholder:text-[var(--color-surface-text-secondary)] focus:outline-none focus:ring-0 disabled:opacity-50 ${PILL_INPUT_HEIGHT_CLASS}`
              }
            />
          </div>
          {models && models.length > 0 && onModelChange && (
            <select
              aria-label={t('aiStudio.conversation.modelLabel')}
              className="mb-0.5 min-w-0 max-w-[140px] shrink-0 rounded-full border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2 py-1.5 text-sm text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-primary-text)]"
              value={selectedModelId ?? ''}
              disabled={disabled}
              onChange={(e) => onModelChange(e.target.value)}
            >
              {models.map((m) => (
                <option key={m.id} value={m.id} disabled={m.disabled}>
                  {m.label}
                </option>
              ))}
            </select>
          )}
          <button
            type="submit"
            disabled={!canSend}
            title={t('aiStudio.conversation.send')}
            aria-label={t('aiStudio.conversation.send')}
            className="mb-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            <SendArrowIcon />
          </button>
        </div>
      </form>
    );
  }

  return (
    <form
      className={`flex-shrink-0 border-t border-[var(--color-border)] p-3 ${className}`}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="flex flex-col overflow-hidden rounded-[1.25rem] border-2 border-[var(--color-border)] bg-[var(--color-surface-bg)] shadow-sm">
        <label htmlFor="ai-studio-composer" className="sr-only">
          {t('aiStudio.conversation.composerLabel')}
        </label>
        <textarea
          id="ai-studio-composer"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={placeholder ?? t('aiStudio.conversation.placeholder')}
          rows={3}
          disabled={disabled}
          className={`w-full resize-none overflow-y-auto border-0 bg-transparent px-3 py-2.5 text-base text-[var(--color-surface-text)] placeholder:text-[var(--color-surface-text-secondary)] focus:outline-none focus:ring-0 disabled:opacity-50 ${CARD_INPUT_HEIGHT_CLASS}`}
        />
        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--color-border)] px-2 py-1.5">
          {models && models.length > 0 && onModelChange && (
            <select
              aria-label={t('aiStudio.conversation.modelLabel')}
              className="max-w-[14rem] rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2 py-1.5 text-base text-[var(--color-surface-text)]"
              value={selectedModelId ?? ''}
              disabled={disabled}
              onChange={(e) => onModelChange(e.target.value)}
            >
              {models.map((m) => (
                <option key={m.id} value={m.id} disabled={m.disabled}>
                  {m.label}
                </option>
              ))}
            </select>
          )}
          <div className="min-w-2 flex-1" />
          <button
            type="submit"
            disabled={!canSend}
            className="shrink-0 rounded-xl bg-[var(--color-accent-bg)] px-4 py-2 text-base font-medium text-[var(--color-accent-text)] transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-50"
          >
            {t('aiStudio.conversation.send')}
          </button>
        </div>
      </div>
    </form>
  );
};
